import { useCallback, useContext } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import gtag from '../analytics/gtag';
import { CartContext } from '../cart/CartContext';
import ErrorBox from '../generic/ErrorBox';
import Icon from '../generic/Icon';
import { TrackingContext } from '../generic/TrackingContext';
import ForgivingNumberInput from '../generic/forms/ForgivingNumberInput';
import { useAsyncAction } from '../utils/useAsyncAction';
import usePropState from '../utils/usePropState';
import * as style from './QuantityPicker.module.css';

interface Props {
    onQuantityChanged?(): Promise<unknown>;
    quantity: number;
    entryNumber: number;
    maxQuantity?: number;
}

export default function QuantityPicker({ onQuantityChanged, entryNumber, quantity, maxQuantity }: Props) {
    const [qty, setQty] = usePropState(quantity);
    const cart = useContext(CartContext);
    const { item_id, index, item_list_id, item_list_name, value } = useContext(TrackingContext);

    const [updateCart, isLoading, error] = useAsyncAction(async (entryNumber: number, qty: number, oldQty: number) => {
        try {
            const { quantityAdded } = qty === 0 ? await cart.deleteCartEntry(entryNumber) : await cart.updateCartEntry(entryNumber, qty);

            if (quantityAdded < 0) {
                gtag('event', 'remove_from_cart', {
                    items: [{ item_id, index, quantity: -quantityAdded }],
                    item_list_id,
                    item_list_name,
                });
            } else {
                gtag('event', 'add_to_cart', {
                    items: [{ item_id, index, quantity: quantityAdded }],
                    item_list_id,
                    item_list_name,
                    value,
                });
            }
            await onQuantityChanged?.();
        } catch (error: any) {
            setQty(oldQty);
            throw error;
        }
    });

    const updateCartDebounced = useDebounceCallback(updateCart, 750);

    const set = useCallback(
        (qty: number) => {
            if (qty < 0) {
                qty = 0;
            } else if (maxQuantity && qty > maxQuantity) {
                qty = maxQuantity;
            }

            setQty(qty);
            updateCartDebounced(entryNumber, qty, quantity);
        },
        [entryNumber, maxQuantity, quantity],
    );

    return (
        <div className={style.container}>
            <div className={style.quantityPicker}>
                <button type="button" disabled={isLoading || qty < 1} title="Reduser antall" aria-label="Reduser antall" onClick={() => set(qty - 1)}>
                    <Icon className="icon-decrease" />
                </button>
                <ForgivingNumberInput
                    aria-label="Antall du ønsker å kjøpe"
                    className={isLoading ? style.busy : undefined}
                    value={qty}
                    min={0}
                    max={maxQuantity}
                    onChange={set}
                />
                <button
                    type="button"
                    disabled={isLoading || (!!maxQuantity && qty >= maxQuantity)}
                    title="Øk antall"
                    aria-label="Øk antall"
                    onClick={() => set(qty + 1)}
                >
                    <Icon className="icon-increase" />
                </button>
            </div>
            <ErrorBox errors={error} />
        </div>
    );
}
